import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { baseUrl, api_service_path } from "./utils"


export const examAction = createApi({
    reducerPath: "examApi",
    baseQuery: fetchBaseQuery({
        baseUrl: `${baseUrl}/${api_service_path.exam}`,
        prepareHeaders: (headers) => {
            const current_user = localStorage.getItem("current_user") ? JSON.parse(localStorage.getItem("current_user")) : null
            if (current_user?.token) {
                headers.set(
                    "Authorization",
                    `Bearer ${current_user.token}`
                )
            }
            return headers
        }
    }),
    tagTypes: ["exam"],
    endpoints: (builder) => ({
        createExam: builder.mutation({
            query: (data) => ({
                url: "/create",
                method: "POST",
                body: data
            })
        }),
        getAllExams: builder.query({
            query: ({
                school_id,
                class_id
            }) => ({
                url: class_id ? `/class_timetable/${school_id}?class_id=${class_id}` : `/class_timetable/${school_id}`,
                method: "GET"
            })
        }),
        createTimetable: builder.mutation({
            query: (data) => ({
                url: `/create_timetable`,
                method: "POST",
                body: data
            })
        }),
        getAllTimetable: builder.query({
            query: ({
                class_id, school_id
            }) => ({
                url: `/all_exam_timetables/${class_id}/${school_id}`,
                method: "GET"
            })
        }),
        addSujectToTimetable: builder.mutation({
            query: ({
                id,
                data
            }) => ({
                url: `/update_timetable/${id}`,
                method: "PUT",
                body: data
            })
        }),
        getAllExamsTimetable: builder.query({
            query: ({
                school_id
            }) => ({
                url: `/all_exam_timetable/${school_id}`,
                method: "GET"
            })
        }),
        deleteExamination: builder.mutation({
            query: ({
                id
            }) => ({
                url: `/delete_exam/${id}`,
                method: "DELETE"
            })
        }),
        editExaminationName: builder.mutation({
            query: ({
                id,
                exam_name
            }) => ({
                url: `/update_exam/${id}/${exam_name}`,
                method: "PUT",
                body: { exam_name }
            })
        }),
        // Prefilled .xlsx with the class's subjects and any saved schedule
        downloadTimetableTemplate: builder.mutation({
            query: (exam_id) => ({
                url: `/timetable_template/${exam_id}`,
                method: "GET",
                responseHandler: (res) => (res.ok ? res.blob() : res.json())
            })
        }),
        // dry_run=true validates and returns a preview; false saves it
        importTimetable: builder.mutation({
            query: ({ exam_id, file, dry_run = true, mode = "merge" }) => {
                const formData = new FormData()
                formData.append("file", file)
                return {
                    url: `/import_timetable/${exam_id}`,
                    method: "POST",
                    params: { dry_run, mode },
                    body: formData
                }
            }
        }),
        downloadResultTemplate: builder.query({
            query: ({
                school_ref,
                class_ref,
                exam_ref
            }) => ({
                url: `/result_template/${school_ref}/${class_ref}/${exam_ref}`,
                method: "GET",
                responseHandler: (res) => {
                    if (res.ok) {
                        return res.blob()
                    } else {
                        return res.json()
                    }
                }
            })
        }),
        downloadSubjectWiseResultImportTemplate: builder.query({
            query: ({
                school_ref,
                class_ref,
                exam_ref,
                subject_name
            }) => ({
                url: `/subject_wise_result_template/${school_ref}/${class_ref}/${exam_ref}/${subject_name}`,
                method: "GET",
                responseHandler: (res) => {
                    if (res.ok) {
                        return res.blob()
                    } else {
                        return res.json()
                    }
                }
            })
        }),
        downloadMinorSubjectResultTemplate:builder.query({
            query:({
                school_id,
                class_id
            })=> ({
                url:`/minor-subject-template/${school_id}/${class_id}`,
                method:"GET",
                responseHandler:(res)=> {
                    if(res.ok){
                        return res.blob()
                    }else{
                        return res.json()
                    }
                }
            })
        }),
        importResult: builder.mutation({
            query: (file) => {
                const formData = new FormData();
                formData.append("file", file);

                return {
                    url: "/import_result",     // FastAPI endpoint
                    method: "POST",
                    body: formData,
                    responseHandler: (res) => res.json()
                }
            }
        }),
        publishResults: builder.mutation({
            query: (data) => ({
                url: "/publish_result",
                method: "POST",
                body: data
            })
        }),
        publishMinorSubjectResults: builder.mutation({
            query: (data) => ({
                url: "/publish_minor_subject_result",
                method: "POST",
                body: data
            })
        }),
        viewExamResultDetails: builder.query({
            query: ({ school_id, class_id, exam_id }) => ({
                url: `/view_result/${school_id}/${class_id}/${exam_id}`,
                method: "GET",
            }),
        }),
        seeStudentResult: builder.mutation({
            query: (data) => ({
                url: "/get_student_result",
                method: "POST",
                body: data,
                responseHandler: async(e) => {
                    if (e.ok) {
                        const contentTyep = e.headers.get("content-type")
                        if (contentTyep?.includes("application/pdf")) {
                            return {
                                type: "pdf",
                                data: await e.blob(),
                            };
                        }else {
                            return {
                                type:"json",
                                data: await e.json()
                            }
                        }
                    } else {
                        return e.json()
                    }
                }
            })
        }),
        examinationAdmitCard: builder.mutation({
            query: (data) => ({
                url: "/get_student_admit",
                method: "POST",
                body: data,
                responseHandler: (res) => {
                    if (res.ok) {
                        return res.blob()
                    } else {
                        return res?.json()
                    }
                }
            })
        }),
        bulkAdmitCard: builder.query({
            query: ({
                school_ref,
                class_ref,
                exam_ref
            }) => ({
                url: `/bulk-admit-download/${school_ref}/${class_ref}/${exam_ref}`,
                method: "GET",
                responseHandler: (res) => {
                    if (res.ok) {
                        return res.blob()
                    } else {
                        return res?.json()
                    }
                }
            })
        }),
        getStudentAdmitDetails: builder.query({
            query: ({
                school_id,
                class_id,
                exam_id,
                student_id
            }) => ({
                url: `/student-admit/${school_id}/${class_id}/${exam_id}/${student_id}`,
                method: "GET"
            })
        }),
        getExamMarkConfiguration: builder.query({
            query: ({ exam_id }) => ({
                url: `/exam-mark-configuration/${exam_id}`,
                method: "GET"
            })
        }),
        configureExamMarkCriteria: builder.mutation({
            query: (data) => ({
                url: "/exam-marks-config",
                method: "POST",
                body: data
            })
        }),
        // Same body as configureExamMarkCriteria; the exam is matched by its refs
        editExamMarkCriteria: builder.mutation({
            query: (data) => ({
                url: "/exam-marks-config",
                method: "PUT",
                body: data
            })
        }),
        updateStudentMarks: builder.mutation({
            query: (data) => ({
                url: "/edit_etudent_marks",
                method: "PUT",
                body: data
            })
        })
    })
})

export const {
    useCreateExamMutation,
    useLazyGetAllExamsQuery,
    useCreateTimetableMutation,
    useLazyGetAllTimetableQuery,
    useAddSujectToTimetableMutation,
    useLazyGetAllExamsTimetableQuery,
    useDeleteExaminationMutation,
    useEditExaminationNameMutation,
    useLazyDownloadResultTemplateQuery,
    useImportResultMutation,
    usePublishResultsMutation,
    useLazyViewExamResultDetailsQuery,
    useSeeStudentResultMutation,
    useExaminationAdmitCardMutation,
    useLazyBulkAdmitCardQuery,

    useLazyGetStudentAdmitDetailsQuery,

    useLazyGetExamMarkConfigurationQuery,
    useConfigureExamMarkCriteriaMutation,
    useEditExamMarkCriteriaMutation,
    useLazyDownloadSubjectWiseResultImportTemplateQuery,
    useUpdateStudentMarksMutation,

    useLazyDownloadMinorSubjectResultTemplateQuery,
    usePublishMinorSubjectResultsMutation,

    useDownloadTimetableTemplateMutation,
    useImportTimetableMutation
} = examAction